import './TelemetryPanel.css'
import { useApi } from '@/hooks/useApi'
import { useInterval } from '@/hooks/useInterval'
import { formatDecimal } from '@/utilities/formatters'
import { telemetryQuerySpec, type TelemetryRow } from 'aevocam-rest-api-spec'
import { useCallback, useEffect, useState } from 'react'
import { Spinner } from '@/components/graphics/Spinner'
import { TelemetrySparkline } from './TelemetrySparkline'
import {
  rowsToTelemetrySeries,
  TELEMETRY_BUCKET_COUNT,
  TELEMETRY_DAILY_BUCKET_COUNT,
  TELEMETRY_DAILY_PERIOD_SEC,
  TELEMETRY_PERIOD_SEC,
  TELEMETRY_REFRESH_MS,
  telemetryDailyWindow,
  telemetryWindow,
  type TelemetrySeries,
} from './telemetryHelpers'

export function TelemetryPanel() {
  const api = useApi({
    requireLogin: true,
    adminOnly: true,
  })

  const [series, setSeries] = useState<TelemetrySeries[]>([])
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const { from: hourlyFrom, to: hourlyTo, now } = telemetryWindow()
    const { from: dailyFrom, to: dailyTo } = telemetryDailyWindow()

    const res = await api.request(telemetryQuerySpec, {
      body: {
        queries: {
          hourly: {
            period: [TELEMETRY_PERIOD_SEC],
            startedAtFrom: hourlyFrom.toISOString(),
            startedAtTo: hourlyTo.toISOString(),
          },
          daily: {
            period: [TELEMETRY_DAILY_PERIOD_SEC],
            startedAtFrom: dailyFrom.toISOString(),
            startedAtTo: dailyTo.toISOString(),
          },
        },
      },
    })

    const rows = Object.values(res.data?.results ?? {})
      .flat() as TelemetryRow[]

    setSeries(rowsToTelemetrySeries(rows, hourlyFrom, hourlyTo, dailyFrom, dailyTo, now))
    setLoading(false)
  }, [api])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useInterval(refresh, TELEMETRY_REFRESH_MS)

  return (
    <div className='TelemetryPanel Cutout Wrapped'>
      <h2>Telemetry</h2>

      {loading && series.length === 0 && (
        <Spinner />
      )}

      {!loading && series.length === 0 && (
        <p className='Empty'>No telemetry data yet.</p>
      )}

      {series.length > 0 && (
        <div className='MetricGrid'>
          {series.map(item => (
            <div key={item.key} className='MetricCard'>
              <div className='Label'>{item.label}</div>
              <div className='Section'>
                <p className='Caption'>{TELEMETRY_BUCKET_COUNT} hours</p>
                <div className='ValueRow'>
                  <span className='Value'>{formatDecimal(Math.round(item.hourlyAverage))}</span>
                  <span className='Unit'>/ hour</span>
                </div>
                <TelemetrySparkline
                  values={item.buckets}
                  bucketFrom={item.bucketFrom}
                />
              </div>
              <div className='Section DailySection'>
                <p className='Caption'>{TELEMETRY_DAILY_BUCKET_COUNT} days</p>
                <div className='ValueRow'>
                  <span className='Value Daily'>{formatDecimal(Math.round(item.dailyAverage))}</span>
                  <span className='Unit'>/ day</span>
                </div>
                <TelemetrySparkline
                  values={item.dailyBuckets}
                  bucketFrom={item.dailyBucketFrom}
                  periodSec={TELEMETRY_DAILY_PERIOD_SEC}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
